// Location detection utilities
export const locationUtils = {
  // Get user's country code using IP geolocation
  getUserCountry: async () => {
    try {
      // Try to get country from IP geolocation
      const response = await fetch('https://ipapi.co/json/');
      const data = await response.json();
      
      if (data.country_code) {
        return {
          countryCode: data.country_code,
          countryName: data.country_name,
          currency: data.currency,
          success: true
        };
      }
      
      throw new Error('Could not determine country from IP');
    } catch (error) {
      console.warn('Failed to detect location from IP:', error);
      
      // Fallback: try to get from browser language/locale
      const fallbackCountry = locationUtils.getCountryFromBrowser();
      
      return {
        countryCode: fallbackCountry.countryCode,
        countryName: fallbackCountry.countryName,
        currency: fallbackCountry.currency,
        success: false,
        fallback: true
      };
    }
  },

  // Fallback method using browser locale
  getCountryFromBrowser: () => {
    const locale = navigator.language || navigator.userLanguage || 'en-US';
    const countryCode = locale.split('-')[1] || 'US';
    
    // Map common country codes to currencies
    const currencyMap = {
      'US': 'USD',
      'IN': 'INR',
      'CA': 'CAD',
      'GB': 'GBP',
      'AU': 'AUD',
      'EU': 'EUR',
      'JP': 'JPY',
      'CN': 'CNY',
      'BR': 'BRL',
      'MX': 'MXN'
    };
    
    const countryNames = {
      'US': 'United States',
      'IN': 'India',
      'CA': 'Canada',
      'GB': 'United Kingdom',
      'AU': 'Australia',
      'EU': 'European Union',
      'JP': 'Japan',
      'CN': 'China',
      'BR': 'Brazil',
      'MX': 'Mexico'
    };
    
    return {
      countryCode,
      countryName: countryNames[countryCode] || 'Unknown',
      currency: currencyMap[countryCode] || 'USD'
    };
  },

  // Get cached country code from localStorage
  getCachedCountry: () => {
    try {
      const cached = localStorage.getItem('user_country');
      if (cached) {
        const data = JSON.parse(cached);
        // Check if cache is less than 24 hours old
        if (Date.now() - data.timestamp < 24 * 60 * 60 * 1000) {
          return data;
        }
      }
    } catch (error) {
      console.warn('Failed to read cached country:', error);
    }
    return null;
  },

  // Cache country data
  cacheCountry: (countryData) => {
    try {
      const dataToCache = {
        ...countryData,
        timestamp: Date.now()
      };
      localStorage.setItem('user_country', JSON.stringify(dataToCache));
    } catch (error) {
      console.warn('Failed to cache country data:', error);
    }
  },

  // Clear cached country data
  clearCachedCountry: () => {
    try {
      localStorage.removeItem('user_country');
      console.log('Location cache cleared successfully');
      return true;
    } catch (error) {
      console.warn('Failed to clear location cache:', error);
      return false;
    }
  },

  // Main function to get user country with caching
  detectUserCountry: async () => {
    // First try to get from cache
    const cached = locationUtils.getCachedCountry();
    if (cached) {
      return cached;
    }

    // If not cached, detect and cache
    const countryData = await locationUtils.getUserCountry();
    locationUtils.cacheCountry(countryData);
    
    return countryData;
  },

  // Force refresh location detection (ignores cache)
  forceRefreshLocation: async () => {
    // Clear cache first
    locationUtils.clearCachedCountry();
    
    // Detect and cache new location
    const countryData = await locationUtils.getUserCountry();
    locationUtils.cacheCountry(countryData);
    
    return countryData;
  },

  // Get country and currency based on payment gateway
  getCountryAndCurrencyForGateway: (countryCode) => {
    const gatewayMap = {
      'IN': { country_code: 'IN', currency_code: 'INR', gateway: 'razorpay' },
      'US': { country_code: 'US', currency_code: 'USD', gateway: 'stripe' },
      'CA': { country_code: 'CA', currency_code: 'CAD', gateway: 'stripe' },
      'GB': { country_code: 'GB', currency_code: 'GBP', gateway: 'stripe' },
      'AU': { country_code: 'AU', currency_code: 'AUD', gateway: 'stripe' },
      'EU': { country_code: 'EU', currency_code: 'EUR', gateway: 'stripe' },
      'JP': { country_code: 'JP', currency_code: 'JPY', gateway: 'stripe' },
      'CN': { country_code: 'CN', currency_code: 'CNY', gateway: 'stripe' },
      'BR': { country_code: 'BR', currency_code: 'BRL', gateway: 'stripe' },
      'MX': { country_code: 'MX', currency_code: 'MXN', gateway: 'stripe' }
    };
    
    return gatewayMap[countryCode] || gatewayMap['US'];
  }
}; 